/**
 * V8 게임 토큰 발급기. Bearer JWT (V8 access JWT 또는 Privy access JWT) 를 verse 별
 * 단명 게임 토큰으로 교환한다.
 *
 * 어느 bearer 든 인증 endpoint 와 응답 형식이 동일 — V8 access / Privy 의 차이는 호출자가
 * 어떤 JWT 를 넘기느냐 뿐이다.
 *
 * SDK `auth` 필드 형식 = base64(JSON.stringify({msg, signature})).
 * REST 응답은 `{ gameAccessToken: { msg, signature } }` 이므로 SDK 형식으로 재인코딩한다.
 *
 * Browser + Node 양쪽에서 동작 — `globalThis.atob/btoa` 사용.
 */

import { parseV8GameToken } from './parse-token';
import type { IssuedGameToken } from './types';

const TAG = '[v8-client/token-issuer]';
const TOKEN_API_BASE = 'https://v8-meme-api.verse8.io/v1/auth/game-token';

interface GameAccessTokenResponse {
  gameAccessToken: { msg: string; signature: string };
}

export async function issueGameToken(
  verseId: string,
  bearerToken: string,
  fetchImpl: typeof fetch = fetch
): Promise<IssuedGameToken> {
  const url = `${TOKEN_API_BASE}/${encodeURIComponent(verseId)}`;
  const res = await fetchImpl(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${bearerToken}` },
  });

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`${TAG} issue failed: HTTP ${res.status} ${body.slice(0, 200)}`);
  }

  const data = (await res.json()) as GameAccessTokenResponse;
  if (!data?.gameAccessToken?.msg || !data?.gameAccessToken?.signature) {
    throw new Error(`${TAG} unexpected response shape: ${JSON.stringify(data).slice(0, 200)}`);
  }

  return {
    authToken: encodeAuthToken(data.gameAccessToken),
    expMs: parseExpMs(data.gameAccessToken.msg),
  };
}

/** `{msg, signature}` 를 SDK auth 형식 base64 문자열로 인코딩. */
export function encodeAuthToken(gameAccessToken: { msg: string; signature: string }): string {
  return globalThis.btoa(JSON.stringify(gameAccessToken));
}

/**
 * inner msg 의 exp (Unix seconds) 를 ms 로 반환.
 * shared parser 를 재활용하기 위해 fake outer envelope 로 한 번 감싼다.
 */
export function parseExpMs(innerMsgBase64: string): number {
  const outer = globalThis.btoa(JSON.stringify({ msg: innerMsgBase64, signature: 'unused' }));
  return parseV8GameToken(outer).exp * 1000;
}
